import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Roles } from "~enums/roles";
import { useApi } from "~hooks/apiHooks/useApi";
import type { TransformedFixtureRoster } from "~pages/Roster/FixtureRosterListPage/types/transformedFixtureRoster.ts";
import { transformFixtureRoster } from "~pages/Roster/FixtureRosterListPage/utilities/transformFixtureRoster.ts";
import type { FixtureRosterResponse } from "~types/fixtureRoster";
import type { RoleResponse } from "~types/role";
import { removeEmptyFields } from "~utils/removeEmptyFields.ts";

type Props = {
  customParams: Record<string, string>;
  entityId?: string;
  fixtureId?: string;
};

const useFetchFixtureRoster = ({ customParams, entityId, fixtureId }: Props) => {
  const request = useApi();
  const { organizationId, sport, seasonId } = useParams();

  const params = new URLSearchParams(removeEmptyFields(customParams) as Record<string, string>);

  const captainParams = new URLSearchParams({
    limit: "1000",
    offset: "0",
    role: Roles.Captain,
    ...(entityId && { entityId }),
  });

  const url = `${sport}/o/${organizationId}/fixtures/${fixtureId}/entities/${entityId}/roster?${params.toString()}`;
  const rolesUrl = `${sport}/o/${organizationId}/fixtures/${fixtureId}/roles?${captainParams.toString()}`;

  return useQuery<Array<TransformedFixtureRoster>>({
    queryKey: ["fixtureRoster", sport, organizationId, seasonId, entityId, fixtureId, params.toString()],
    queryFn: async () => {
      const [response, captainResponse] = await Promise.all([request<FixtureRosterResponse>(url), request<RoleResponse>(rolesUrl)]);
      return transformFixtureRoster(response, captainResponse);
    },
    enabled: !!entityId && !!fixtureId,
  });
};

export default useFetchFixtureRoster;
